import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { User } from "@/schema/authentication";
import { Organization } from "@/schema/organization";
import { CustomerProfile, CompanyProfile } from "@/schema/profile";

interface Profile {
  customer: CustomerProfile | null;
  company: CompanyProfile | null;
}

interface AuthenticationStore {
  user: User | null;
  organization: Organization | null;
  profile: Profile;

  reauthenticate: () => void;
  authenticate: (user: User, organization: Organization | null, profile: Profile) => void;

  setUser: (user: User) => void;
  updateUser: (user: Partial<User>) => void;

  setOrganization: (organization: Organization) => void;
  updateOrganization: (organization: Partial<Organization>) => void;

  setProfile: (profile: Profile) => void;
  updateProfile: (profile: Partial<Profile>) => void;

  setCustomerProfile: (customerProfile: CustomerProfile) => void;
  updateCustomerProfile: (customerProfile: Partial<CustomerProfile>) => void;

  setCompanyProfile: (companyProfile: CompanyProfile) => void;
  updateCompanyProfile: (companyProfile: Partial<CompanyProfile>) => void;
}

export const useAuthenticationStore = create<AuthenticationStore>()(
  immer((set) => ({
    user: null,
    organization: null,
    profile: {
      customer: null,
      company: null,
    },

    reauthenticate: () => {
      set((state) => {
        state.user = null;
        state.organization = null;
        state.profile = {
          customer: null,
          company: null,
        };
      });
    },
    authenticate: (user: User, organization: Organization | null, profile: Profile) => {
      set((state) => {
        state.user = user;
        state.organization = organization;
        state.profile = profile;
      });
    },

    setUser: (user: User) => {
      set((state) => {
        state.user = user;
      });
    },
    updateUser: (user: Partial<User>) => {
      set((state) => {
        if (state.user) {
          state.user = { ...state.user, ...user };
        }
      });
    },

    setOrganization: (organization: Organization) => {
      set((state) => {
        state.organization = organization;
      });
    },
    updateOrganization: (organization: Partial<Organization>) => {
      set((state) => {
        if (state.organization) {
          state.organization = { ...state.organization, ...organization };
        }
      });
    },

    setProfile: (profile: Profile) => {
      set((state) => {
        state.profile = profile;
      });
    },
    updateProfile: (profile: Partial<Profile>) => {
      set((state) => {
        state.profile = { ...state.profile, ...profile };
      });
    },

    setCustomerProfile: (customerProfile: CustomerProfile) => {
      set((state) => {
        state.profile.customer = customerProfile;
      });
    },
    updateCustomerProfile: (customerProfile: Partial<CustomerProfile>) => {
      set((state) => {
        if (state.profile.customer) {
          state.profile.customer = { ...state.profile.customer, ...customerProfile };
        }
      });
    },

    setCompanyProfile: (companyProfile: CompanyProfile) => {
      set((state) => {
        state.profile.company = companyProfile;
      });
    },
    updateCompanyProfile: (companyProfile: Partial<CompanyProfile>) => {
      set((state) => {
        if (state.profile.company) {
          state.profile.company = { ...state.profile.company, ...companyProfile };
        }
      });
    },
  }))
);
